import { Checkbox } from '@/components/forms/Checkbox';
import Text from '@/components/Text';
import { getGraphEntitlement } from '@squaredup/tenants';
import { LinkWrapper } from 'components/LinkWrapper';
import { toPermittedLink } from 'dashboard-engine/util/drilldown';
import { useTier } from 'queries/hooks/useTier';
import ReactMarkdown from 'react-markdown';
import { Components } from 'react-markdown/src/ast-to-react';

type RemarkPlugins = React.ComponentProps<typeof ReactMarkdown>['remarkPlugins'];

interface MarkdownBaseProps {
    content: string;
    components?: Components;
    /** Additional remark plugins, e.g. remark-gfm for tables/strikethrough */
    remarkPlugins?: RemarkPlugins;
}

/**
 * Get the indentation for a list element based on a given depth
 * @param depth List depth
 * @returns Styles
 */
export const getIndentation = (depth: number) => ({ marginLeft: depth * 20, ...(depth > 0 && { marginTop: 5 }) });

/**
 * Defined at module scope so component identities are stable across renders —
 * inline definitions would remount the entire markdown DOM on every render.
 * Text components are wrapped rather than passed directly so that
 * react-markdown's internal props (e.g. `node`) don't spread onto the DOM.
 */
const MarkdownLink: Components['a'] = ({ href, children }) => {
    const { data: tier } = useTier();
    const isGraphEntitled = getGraphEntitlement(tier).state === 'entitled';

    return (
        <LinkWrapper
            link={toPermittedLink(href as string | undefined, isGraphEntitled)}
            className='text-textLink text-[100%]'
        >
            {children}
        </LinkWrapper>
    );
};

const defaultComponents: Components = {
    a: MarkdownLink,
    h1: ({ children }) => <Text.H1>{children}</Text.H1>,
    h2: ({ children }) => <Text.H2>{children}</Text.H2>,
    h3: ({ children }) => <Text.H3>{children}</Text.H3>,
    h4: ({ children }) => <Text.H4>{children}</Text.H4>,
    h5: ({ children }) => <Text.H5>{children}</Text.H5>,
    p: ({ children }) => <Text.SmallBody>{children}</Text.SmallBody>,
    pre: ({ children }) => (
        <pre className='p-3 overflow-x-auto text-sm border rounded-md bg-modalOutline border-outlinePrimary'>
            {children}
        </pre>
    ),
    ol: ({ children, depth }) => (
        <ol
            className='space-y-1 list-decimal list-inside marker:text-textSecondary marker:mr-2 text-[100%]'
            style={getIndentation(depth)}
        >
            {children}
        </ol>
    ),
    ul: ({ children, depth }) => (
        <ul className='space-y-1 list-disc list-inside text-[100%]' style={getIndentation(depth)}>
            {children}
        </ul>
    ),
    del: ({ children }) => <del className='line-through text-textSecondary'>{children}</del>,
    /** GFM task-list checkboxes — read-only, so always disabled */
    input: ({ checked }) => <Checkbox checked={Boolean(checked)} disabled className='mr-2' />,
    hr: () => <hr className='border-dividerPrimary' />,
    table: ({ children }) => (
        <div className='overflow-x-auto'>
            <table className='w-full text-sm border-collapse'>{children}</table>
        </div>
    ),
    th: ({ children, style }) => (
        <th
            className='px-3 py-2 font-semibold text-left align-top border border-outlinePrimary text-textPrimary'
            style={style}
        >
            {children}
        </th>
    ),
    td: ({ children, style }) => (
        <td className='px-3 py-2 align-top border border-outlinePrimary' style={style}>
            {children}
        </td>
    )
};

/**
 * Generates styled React elements from Markdown, using base styles that match the app
 * Individual components can be overriden using the `components` property.
 * Pass a referentially stable `components` object (module scope or memoised) —
 * a new object of inline components every render remounts the markdown DOM.
 */
export const MarkdownBase = ({ content, components, remarkPlugins }: MarkdownBaseProps) => (
    <ReactMarkdown
        className='space-y-4 break-words'
        remarkPlugins={remarkPlugins}
        components={{
            ...defaultComponents,
            ...components
        }}
    >
        {content}
    </ReactMarkdown>
);
